/**
 * Product Catalog Page
 * Browse all products with filters, sorting, and pagination
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Main, Flex, Grid } from '../../../components/styled/Layout';
import { Card, CardContent } from '../../../components/styled/Card';
import { PrimaryButton, SecondaryButton } from '../../../components/styled/Button';
import productService from '../services/productService';
import { useCart } from '../contexts/CartContext';
import useShopFeatures from '../hooks/useShopFeatures';
import { getRatingValue, getRatingCount, renderStars, formatPrice } from '../utils/productHelpers';
import './Shop.css';

const ProductCatalog = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addItem: addToCart } = useCart();
  const features = useShopFeatures();

  // Filter and sort state
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [minPrice, setMinPrice] = useState(searchParams.get('minPrice') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('maxPrice') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'name');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [totalPages, setTotalPages] = useState(1);

  // Sync state with URL parameters when they change
  useEffect(() => {
    setSelectedCategory(searchParams.get('category') || '');
    setMinPrice(searchParams.get('minPrice') || '');
    setMaxPrice(searchParams.get('maxPrice') || '');
    setSortBy(searchParams.get('sort') || 'name');
    setCurrentPage(parseInt(searchParams.get('page') || '1'));
  }, [searchParams]);

  useEffect(() => {
    loadProducts();
  }, [selectedCategory, minPrice, maxPrice, sortBy, currentPage]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 12,
        category: selectedCategory,
        minPrice: minPrice,
        maxPrice: maxPrice,
        sortBy: sortBy
      };
      
      const response = await productService.getProducts(params);
      setProducts(response.items || []);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const cats = await productService.getCategories();
      setCategories(cats || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    updateURL({ category, page: 1 });
  };

  const handleSortChange = (e) => {
    const newSort = e.target.value;
    setSortBy(newSort);
    setCurrentPage(1);
    updateURL({ sort: newSort, page: 1 });
  };

  const handlePriceFilter = () => {
    setCurrentPage(1);
    updateURL({ minPrice, maxPrice, page: 1 });
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setMinPrice('');
    setMaxPrice('');
    setSortBy('name');
    setCurrentPage(1);
    setSearchParams({});
  };

  const updateURL = (params) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  const sortProducts = (items) => {
    return [...items].sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    });
  };

  const sortedProducts = sortProducts(products);

  return (
    <Container>
      <Main>
        <div className="shop-page catalog-page">
          <h1>Product Catalog</h1>
          
          <Flex gap="var(--space-4)">
            {/* Filters Sidebar */}
            {features.advancedFiltering && (
              <aside className="filters-sidebar">
                <Card>
                  <CardContent>
                    <h3>Filters</h3>
                    
                    {/* Category Filter */}
                    <div className="filter-group">
                      <label>Category</label>
                      <select 
                        value={selectedCategory} 
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        className="filter-select"
                      >
                        <option value="">All Categories</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Price Filter */}
                    <div className="filter-group">
                      <label>Price Range</label>
                      <Flex gap="8px">
                        <input
                          type="number"
                          placeholder="Min"
                          value={minPrice}
                          onChange={(e) => setMinPrice(e.target.value)}
                          className="price-input"
                        />
                        <span>-</span>
                        <input
                          type="number"
                          placeholder="Max"
                          value={maxPrice}
                          onChange={(e) => setMaxPrice(e.target.value)}
                          className="price-input"
                        />
                      </Flex>
                      <PrimaryButton onClick={handlePriceFilter} size="small" style={{ marginTop: '8px', width: '100%' }}>
                        Apply
                      </PrimaryButton>
                    </div>

                    <SecondaryButton onClick={clearFilters} style={{ width: '100%' }}>
                      Clear Filters
                    </SecondaryButton>
                  </CardContent>
                </Card>
              </aside>
            )}

            {/* Products Area */}
            <div className="products-area">
              {/* Sort & Results Info */}
              <Flex justify="space-between" align="center" style={{ marginBottom: '20px' }}>
                <div className="results-info">
                  {products.length} Products
                </div>
                <div className="sort-controls">
                  <label>Sort by:</label>
                  <select value={sortBy} onChange={handleSortChange} className="sort-select">
                    <option value="name">Name</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="rating">Highest Rated</option>
                  </select>
                </div>
              </Flex>

              {/* Product Grid */}
              {loading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading products...</p>
                </div>
              ) : sortedProducts.length === 0 ? (
                <div className="empty-state">
                  <h3>No products found</h3>
                  <p>Try adjusting your filters or search criteria</p>
                  <SecondaryButton onClick={clearFilters}>Clear Filters</SecondaryButton>
                </div>
              ) : (
                <>
                  <div className="product-grid">
                    {sortedProducts.map(product => (
                      <div 
                        key={product.id} 
                        className="product-card-wrapper" 
                        onClick={() => navigate(`/shop/product/${product.id}`)}
                      >
                        <Card className="product-card">
                          <div className="product-image-container">
                            <img src={product.image} alt={product.name} className="product-image" />
                          </div>
                          <CardContent>
                            <h3 className="product-name">{product.name}</h3>
                              {product.rating && (
                                <div className="product-rating">
                                  <span className="stars">
                                    {renderStars(product.rating)}
                                  </span>
                                  {getRatingCount(product.rating, product.ratingCount) > 0 && (
                                    <span className="rating-count">({getRatingCount(product.rating, product.ratingCount)})</span>
                                  )}
                                </div>
                              )}
                            <p className="price">${formatPrice(product.price)}</p>
                            {features.stockIndicators && product.stockQty !== undefined && (
                              <div className={`stock-badge ${product.stockQty > 0 ? 'in-stock' : 'out-of-stock'}`}>
                                {product.stockQty > 0 ? `${product.stockQty} in stock` : 'Out of stock'}
                              </div>
                            )}
                            <PrimaryButton 
                              onClick={(e) => {
                                e.stopPropagation();
                                if (product.stockQty > 0) {
                                  addToCart(product);
                                  alert(`✅ ${product.name} added to cart!\n\nClick the 🛒 cart icon in the top-right corner.`);
                                }
                              }}
                              disabled={product.stockQty === 0}
                              className="add-to-cart-btn"
                            >
                              {product.stockQty > 0 ? 'Add to Cart' : 'Out of Stock'}
                            </PrimaryButton>
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <Flex justify="center" gap="8px" style={{ marginTop: '32px' }}>
                      <SecondaryButton 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </SecondaryButton>
                      <div className="page-info">
                        Page {currentPage} of {totalPages}
                      </div>
                      <SecondaryButton 
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </SecondaryButton>
                    </Flex>
                  )}
                </>
              )}
            </div>
          </Flex>
        </div>
      </Main>
    </Container>
  );
};

export default ProductCatalog;

